import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import FileUploader from './FileUploader';
import VideoPreview from './VideoPreview';
import VideoDetailsForm, { VideoFormData } from './VideoDetailsForm';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type UploadStep = 'initial' | 'choose-video' | 'verify-instagram' | 'video-preview' | 'video-details' | 'publishing';

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 32px;
  width: 100%;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: transparent;
  border: none;
  color: ${({ $active }) => $active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 16px;
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  padding: 0;
  cursor: pointer;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -16px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ $active }) => $active ? '#9333EA' : 'transparent'};
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  line-height: 1;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  width: 100%;
`;

const TextContent = styled.div`
  flex: 1;
  margin-right: 24px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  background-color: rgba(147, 51, 234, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TipsSection = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
`;

const TipsTitle = styled.h3`
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 16px 0;
`;

const TipsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TipItem = styled.li`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  
  &::before {
    content: '•';
    color: #9333EA;
    font-size: 18px;
    margin-right: 10px;
    line-height: 1;
  }
`;

const Link = styled.a`
  color: #9333EA;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ContentLayout = styled.div`
  display: flex;
  width: 100%;
  gap: 32px;
`;

const LeftColumn = styled.div`
  flex: 1;
`;

const RightColumn = styled.div`
  width: 240px;
  flex-shrink: 0;
`;

const ExampleImage = styled.div`
  width: 100%;
  height: 240px;
  background-color: #F8D7D9;
  border-radius: 8px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ActionButton = styled.button`
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 160px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  
  &:hover {
    background-color: #A855F7;
  }
`;

const VideoSourceOption = styled.div`
  display: flex;
  align-items: center;
  padding: 24px;
  background-color: #2A2438;
  border-radius: 12px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  
  &:hover {
    background-color: #332A45;
  }
`;

const EnterpriseTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(147, 51, 234, 0.1);
  border-radius: 4px;
  padding: 4px 8px;
  margin-top: 8px;
  
  span {
    color: #9333EA;
    font-size: 12px;
    font-weight: 500;
  }
  
  svg {
    margin-right: 4px;
  }
`;

const VideoSourcesContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const OptionIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  background-color: ${props => props.color || '#9333EA'};
`;

const OptionText = styled.span`
  color: white;
  font-size: 18px;
  font-weight: 600;
`;

const StepTitle = styled.h2`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 24px;
  text-align: center;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    color: white;
  }
`;

const VerifyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
`;

const InstagramLogo = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background-color: #2A2438;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 20px;
`;

const VerifyText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  text-align: center;
  max-width: 400px;
  margin: 16px 0 32px 0;
  line-height: 1.5;
`;

const VerifyButton = styled.button`
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  min-width: 160px;
  
  &:hover {
    background-color: #A855F7;
  }
`;

const PublishContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  text-align: center;
`;

const PublishText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin: 8px 0 0 0;
`;

const SuccessIcon = styled.div`
  margin-bottom: 16px;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(147, 51, 234, 0.1);
  border-radius: 50%;
  border-top-color: #9333EA;
  margin-bottom: 24px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState<UploadStep>('initial');
  const [activeTab, setActiveTab] = useState<'product-reel' | 'reel'>('product-reel');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoSource, setVideoSource] = useState<'upload' | 'instagram' | null>(null);
  const [videoDetails, setVideoDetails] = useState<VideoFormData | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);

  const handleActionButtonClick = () => {
    setCurrentStep('choose-video');
  };

  const handleBack = () => {
    if (currentStep === 'choose-video') {
      setCurrentStep('initial');
    } else if (currentStep === 'verify-instagram') {
      setCurrentStep('choose-video');
    } else if (currentStep === 'video-preview') {
      setCurrentStep('choose-video');
    } else if (currentStep === 'video-details') {
      setCurrentStep('video-preview');
    }
  };

  const handleVideoSourceSelect = (source: 'upload' | 'instagram') => {
    setVideoSource(source);
    
    if (source === 'instagram') {
      setCurrentStep('verify-instagram');
    } else {
      setCurrentStep('video-preview');
    }
  };

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    setCurrentStep('video-details');
  };
  
  const handleVideoDetailsSubmit = (data: VideoFormData) => {
    setVideoDetails(data);
    setCurrentStep('publishing');
    setIsPublishing(true);
    
    // Simulate publishing process
    setTimeout(() => {
      setIsPublishing(false);
      // Here you would typically handle the actual upload and publishing
      // For now, we'll just close the modal after a delay
      setTimeout(() => {
        onClose();
      }, 1000);
    }, 2000);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setCurrentStep('video-preview');
  };

  const renderProductReelContent = () => (
    <>
      <ContentContainer>
        <TextContent>
          <Title>Drive conversions on product pages</Title>
          <Subtitle>
            Share reels about a specific product that inspires its traffic to convert.
          </Subtitle>
        </TextContent>
        <IconContainer>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 10C10.1046 10 11 9.10457 11 8C11 6.89543 10.1046 6 9 6C7.89543 6 7 6.89543 7 8C7 9.10457 7.89543 10 9 10Z" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2.67 18.95L7.6 15.64C8.39 15.11 9.53 15.17 10.24 15.78L10.57 16.07C11.35 16.74 12.61 16.74 13.39 16.07L17.55 12.5C18.33 11.83 19.59 11.83 20.37 12.5L22 13.9" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconContainer>
      </ContentContainer>
      
      <ContentLayout>
        <LeftColumn>
          <TipsSection>
            <TipsTitle>Here are some important tips</TipsTitle>
            <TipsList>
              <TipItem>Ensure the product is tagged and visible</TipItem>
              <TipItem>Capture solely vertical content</TipItem>
              <TipItem>
                Tours, reviews, walkthroughs, highlights, social proof, and demonstrations are ideal
              </TipItem>
              <TipItem>
                Follow our <Link href="#">community Guidelines</Link>
              </TipItem>
            </TipsList>
          </TipsSection>
        </LeftColumn>
        
        <RightColumn>
          <ExampleImage>
            <img 
              src="/product-example.jpg" 
              alt="Product example" 
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "https://images.unsplash.com/photo-1526947425960-945c6e72858f?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60";
              }}
            />
          </ExampleImage>
        </RightColumn>
      </ContentLayout>
      
      <ActionButton onClick={handleActionButtonClick}>Create Post</ActionButton>
    </>
  );

  const renderReelContent = () => (
    <>
      <ContentContainer>
        <TextContent>
          <Title>Grow your audience with reels</Title>
          <Subtitle>
            Create short-form vertical videos that engage and grow your audience.
          </Subtitle>
          <EnterpriseTag>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1.33334L10.06 5.50668L14.6667 6.18001L11.3333 9.42668L12.12 14.0133L8 11.8467L3.88 14.0133L4.66667 9.42668L1.33334 6.18001L5.94 5.50668L8 1.33334Z" fill="#9333EA" fillOpacity="0.2" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Enterprise Feature</span>
          </EnterpriseTag>
        </TextContent>
        <IconContainer>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.53 20.4201H6.21C3.05 20.4201 2 18.3201 2 16.2101V7.79008C2 4.63008 3.05 3.58008 6.21 3.58008H12.53C15.69 3.58008 16.74 4.63008 16.74 7.79008V16.2101C16.74 19.3701 15.68 20.4201 12.53 20.4201Z" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M19.52 17.0999L16.74 15.1499V8.83989L19.52 6.88989C20.88 5.93989 22 6.51989 22 8.18989V15.8099C22 17.4799 20.88 18.0599 19.52 17.0999Z" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11.5 11C12.3284 11 13 10.3284 13 9.5C13 8.67157 12.3284 8 11.5 8C10.6716 8 10 8.67157 10 9.5C10 10.3284 10.6716 11 11.5 11Z" stroke="#9333EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </IconContainer>
      </ContentContainer>
      
      <ContentLayout>
        <LeftColumn>
          <TipsSection>
            <TipsTitle>Here are some important tips</TipsTitle>
            <TipsList>
              <TipItem>Keep your reels between 15-30 seconds for optimal engagement</TipItem>
              <TipItem>Use trending sounds and music to increase discoverability</TipItem>
              <TipItem>
                Include captions to make your content accessible and increase watch time
              </TipItem>
              <TipItem>
                Maintain high video quality and good lighting
              </TipItem>
              <TipItem>
                Follow our <Link href="#">community Guidelines</Link>
              </TipItem>
            </TipsList>
          </TipsSection>
        </LeftColumn>
        
        <RightColumn>
          <ExampleImage>
            <img 
              src="/reel-example.jpg" 
              alt="Reel example" 
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60";
              }}
            />
          </ExampleImage>
        </RightColumn>
      </ContentLayout>
      
      <ActionButton onClick={handleActionButtonClick}>Create Reel</ActionButton>
    </>
  );

  const renderInitialStep = () => (
    <>
      <ModalHeader>
        <TabsContainer>
          <Tab 
            $active={activeTab === 'product-reel'} 
            onClick={() => setActiveTab('product-reel')}
          >
            Product Reel
          </Tab>
          <Tab 
            $active={activeTab === 'reel'} 
            onClick={() => setActiveTab('reel')}
          >
            Reel
          </Tab>
        </TabsContainer>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
      </ModalHeader>
      
      <ModalBody>
        {activeTab === 'product-reel' ? renderProductReelContent() : renderReelContent()}
      </ModalBody>
    </>
  );
  
  const renderChooseVideoStep = () => (
    <>
      <ModalHeader>
        <BackButton onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.57 5.93L3.5 12L9.57 18.07" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20.5 12H3.67" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
      </ModalHeader>
      
      <ModalBody>
        <StepTitle>Choose a video</StepTitle>
        
        <VideoSourceOption onClick={() => handleVideoSourceSelect('upload')}>
          <OptionIcon>
            <img src="/upload-icon.svg" alt="Upload" width="32" height="32" />
          </OptionIcon>
          <OptionText>Upload</OptionText>
        </VideoSourceOption>
        
        <VideoSourceOption onClick={() => handleVideoSourceSelect('instagram')}>
          <OptionIcon color="#E1306C">
            <img src="/instagram-icon.svg" alt="Instagram" width="32" height="32" />
          </OptionIcon>
          <OptionText>Instagram</OptionText>
        </VideoSourceOption>
      </ModalBody>
    </>
  );
  
  const renderVerifyInstagramStep = () => (
    <>
      <ModalHeader>
        <BackButton onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.57 5.93L3.5 12L9.57 18.07" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20.5 12H3.67" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
      </ModalHeader>
      
      <ModalBody>
        <VerifyContainer>
          <InstagramLogo>
            <img src="/instagram-icon.svg" alt="Instagram" width="48" height="48" />
          </InstagramLogo>
          
          <StepTitle>Verify Instagram</StepTitle>
          
          <VerifyText>
            Explore Enterprises you can create content for
          </VerifyText>
          
          <VerifyButton onClick={() => setCurrentStep('video-preview')}>
            Verify
          </VerifyButton>
        </VerifyContainer>
      </ModalBody>
    </>
  );
  
  const renderVideoPreviewStep = () => (
    <>
      <ModalHeader>
        <BackButton onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.57 5.93L3.5 12L9.57 18.07" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20.5 12H3.67" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
      </ModalHeader>
      
      <ModalBody>
        <StepTitle>Upload Video</StepTitle>
        <FileUploader onFileSelected={handleFileSelected} />
      </ModalBody>
    </>
  );
  
  const renderVideoDetailsStep = () => (
    <>
      <ModalHeader>
        <BackButton onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.57 5.93L3.5 12L9.57 18.07" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20.5 12H3.67" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
      </ModalHeader>
      
      <ModalBody>
        <StepTitle>Video Details</StepTitle>
        {selectedFile && (
          <VideoPreview file={selectedFile} onRemove={handleRemoveFile} />
        )}
        
        <VideoDetailsForm onSubmit={handleVideoDetailsSubmit} />
      </ModalBody>
    </>
  );
  
  const renderPublishingStep = () => (
    <>
      <ModalHeader>
        <CloseButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </CloseButton>
      </ModalHeader>
      
      <ModalBody>
        <PublishContainer>
          {isPublishing ? (
            <>
              <StepTitle>Publishing your video...</StepTitle>
              <Spinner />
              <PublishText>Please wait while we process your video</PublishText>
            </>
          ) : (
            <>
              <SuccessIcon>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 4C12.96 4 4 12.96 4 24C4 35.04 12.96 44 24 44C35.04 44 44 35.04 44 24C44 12.96 35.04 4 24 4ZM20 34L10 24L12.82 21.18L20 28.34L35.18 13.16L38 16L20 34Z" fill="#4CAF50"/>
                </svg>
              </SuccessIcon>
              <StepTitle>Video published successfully!</StepTitle>
              <PublishText>Your video is now being processed and will be available soon</PublishText>
            </>
          )}
        </PublishContainer>
      </ModalBody>
    </>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'initial':
        return renderInitialStep();
      case 'choose-video':
        return renderChooseVideoStep();
      case 'verify-instagram':
        return renderVerifyInstagramStep();
      case 'video-preview':
        return renderVideoPreviewStep();
      case 'video-details':
        return renderVideoDetailsStep();
      case 'publishing':
        return renderPublishingStep();
      default:
        return renderInitialStep();
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="600px">  
      {renderCurrentStep()}
    </Modal>
  );
};

export default UploadModal;
